import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight, Trophy } from "lucide-react";
import { COMPETITORS } from "@/data/comparisons";
import PlanFinder from "@/components/PlanFinder";

export default function ComparePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    api.get(`/compare/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
    // SEO: set title
    document.title = `DocuMind vs ${slug} — Honest comparison (2026) | DocuMind`;
  }, [slug]);

  if (error) return <Navigate to="/compare" replace />;
  if (!data) return <div className="min-h-screen flex items-center justify-center font-mono-label text-muted-foreground">Loading…</div>;

  const { competitor, feature_matrix, verdict } = data;

  const Cell = ({ v }) => {
    if (typeof v === "boolean") return v
      ? <Check className="w-5 h-5 text-primary" strokeWidth={2.5} />
      : <X className="w-5 h-5 text-muted-foreground/50" strokeWidth={2} />;
    return <span className="text-sm">{v}</span>;
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Editorial header */}
      <section className="paper-noise border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="font-mono-label text-muted-foreground mb-8">The DocuMind Review · Published 2026</div>
          <h1 className="font-serif-display text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-8">
            DocuMind <span className="italic text-muted-foreground">vs</span><br />
            {competitor.name}.
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl leading-relaxed">
            An honest, feature-by-feature breakdown. {competitor.name} is a credible chat-with-PDF tool — but DocuMind is built for people who need multi-document, citation-grade answers at scale.
          </p>
          <div className="mt-10 flex items-center gap-4">
            <Link to="/signup" data-testid="compare-cta-signup">
              <Button size="lg" className="bg-foreground text-background hover:bg-primary rounded-sm btn-invert">
                Try DocuMind free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <span className="font-mono-label text-muted-foreground">7-day refund · No card required</span>
          </div>
        </div>
      </section>

      {/* TL;DR verdict */}
      <section className="border-b border-border bg-muted">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="font-mono-label text-muted-foreground mb-4">Verdict</div>
          <div className="flex items-start gap-6">
            <Trophy strokeWidth={1.3} className="w-12 h-12 text-primary flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight mb-4 leading-tight">
                DocuMind wins on {verdict.documind_wins} capabilit{verdict.documind_wins === 1 ? 'y' : 'ies'} where {competitor.name} falls short.
              </h2>
              <p className="text-lg leading-relaxed text-muted-foreground">{verdict.summary}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
        <div className="font-mono-label text-muted-foreground mb-4">Feature matrix</div>
        <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight mb-10">Side by side.</h2>
        <div className="border border-border">
          <div className="grid grid-cols-3 bg-foreground text-background">
            <div className="p-5 font-mono-label">Feature</div>
            <div className="p-5 font-mono-label text-primary-foreground">DocuMind</div>
            <div className="p-5 font-mono-label text-background/70">{competitor.name}</div>
          </div>
          {feature_matrix.map((row, i) => (
            <div key={i} className="grid grid-cols-3 border-t border-border">
              <div className="p-5 text-sm font-medium">{row.feature}</div>
              <div className="p-5 bg-accent/20"><Cell v={row.documind} /></div>
              <div className="p-5"><Cell v={row.competitor} /></div>
            </div>
          ))}
        </div>
      </section>

      {/* Pros/cons */}
      <section className="border-y border-border bg-muted">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 gap-10">
          <div>
            <div className="font-mono-label text-primary mb-3">DocuMind</div>
            <h3 className="font-serif-display text-3xl mb-6">Where we shine</h3>
            <ul className="space-y-3">
              {data.documind.pros.map((p) => (
                <li key={p} className="flex items-start gap-3"><Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span>{p}</span></li>
              ))}
            </ul>
            <div className="font-mono-label text-muted-foreground mt-10 mb-3">Trade-offs</div>
            <ul className="space-y-2 text-muted-foreground">
              {data.documind.cons.map((c) => (<li key={c} className="flex items-start gap-3"><X className="w-4 h-4 mt-1 flex-shrink-0" />{c}</li>))}
            </ul>
          </div>
          <div>
            <div className="font-mono-label text-muted-foreground mb-3">{competitor.name}</div>
            <h3 className="font-serif-display text-3xl mb-6">Where {competitor.name} does well</h3>
            <ul className="space-y-3">
              {competitor.pros.map((p) => (
                <li key={p} className="flex items-start gap-3"><Check className="w-5 h-5 text-foreground/60 mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span>{p}</span></li>
              ))}
            </ul>
            <div className="font-mono-label text-muted-foreground mt-10 mb-3">Trade-offs</div>
            <ul className="space-y-2 text-muted-foreground">
              {competitor.cons.map((c) => (<li key={c} className="flex items-start gap-3"><X className="w-4 h-4 mt-1 flex-shrink-0" />{c}</li>))}
            </ul>
          </div>
        </div>
      </section>

      {/* Who should pick what */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="font-mono-label text-muted-foreground mb-4">The call</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter mb-10">Who should pick which?</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="border border-foreground p-8 bg-accent/20">
            <div className="font-mono-label text-primary mb-3">Pick DocuMind if…</div>
            <ul className="space-y-3 text-base leading-relaxed">
              <li>— You routinely chat across multiple PDFs at once.</li>
              <li>— You want shareable, embeddable chatbots trained on your docs.</li>
              <li>— You need API access to automate document workflows.</li>
              <li>— Citation accuracy matters more than price.</li>
            </ul>
          </div>
          <div className="border border-border p-8">
            <div className="font-mono-label text-muted-foreground mb-3">Pick {competitor.name} if…</div>
            <ul className="space-y-3 text-base leading-relaxed text-muted-foreground">
              <li>— You only chat with one PDF at a time.</li>
              <li>— You don't need to share or embed chatbots.</li>
              <li>— You prefer their specific UI or existing integrations.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Plan finder */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-20">
        <PlanFinder />
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <h2 className="font-serif-display text-4xl lg:text-6xl tracking-tighter mb-6">
            Try DocuMind for 7 days, free.
          </h2>
          <p className="text-background/70 mb-10 max-w-xl mx-auto">If you came for a {competitor.name} alternative, we'd love to earn your bookmark. Upload a PDF and judge for yourself.</p>
          <Link to="/signup" data-testid="compare-footer-cta">
            <Button size="lg" className="bg-background text-foreground hover:bg-primary hover:text-primary-foreground rounded-sm btn-invert px-8 h-14">
              Start free →
            </Button>
          </Link>
        </div>
      </section>

      {/* Other comparisons */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="font-mono-label text-muted-foreground mb-6">More reviews</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-0 border-t border-l border-border">
          {COMPETITORS.filter((c) => c.slug !== slug).map((c) => (
            <Link key={c.slug} to={`/compare/${c.slug}`} data-testid={`compare-more-${c.slug}`} className="grid-cell p-6 hover:bg-muted group">
              <div className="font-mono-label text-muted-foreground mb-2">vs</div>
              <div className="font-serif-display text-2xl flex items-center justify-between">{c.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
            </Link>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
